import React, { useState, useCallback, useRef } from 'react';

interface SwipeGestureOptions {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  minDistance?: number;
}

export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  minDistance = 40,
}: SwipeGestureOptions) {
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const startXRef = useRef<number | null>(null);
  const currentOffsetRef = useRef<number>(0);
  const didDragRef = useRef<boolean>(false);

  const start = (clientX: number) => {
    startXRef.current = clientX;
    currentOffsetRef.current = 0;
    didDragRef.current = false;
    setIsDragging(true);
    setDragOffset(0);
  };

  const move = (clientX: number) => {
    if (startXRef.current === null) return;
    const diff = clientX - startXRef.current;
    if (Math.abs(diff) > 8) didDragRef.current = true;
    currentOffsetRef.current = diff;
    setDragOffset(diff);
  };

  const end = () => {
    if (startXRef.current === null) return;
    const diff = currentOffsetRef.current;
    if (diff < -minDistance) {
      onSwipeLeft();
    } else if (diff > minDistance) {
      onSwipeRight();
    }
    startXRef.current = null;
    currentOffsetRef.current = 0;
    setIsDragging(false);
    setDragOffset(0);
  };

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    start(e.touches[0].clientX);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    move(e.touches[0].clientX);
  }, []);

  const onTouchEnd = useCallback(() => end(), [minDistance, onSwipeLeft, onSwipeRight]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) start(e.clientX);
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (startXRef.current !== null) move(e.clientX);
  }, []);

  const onMouseUp = useCallback(() => {
    if (startXRef.current !== null) end();
  }, [minDistance, onSwipeLeft, onSwipeRight]);

  const onMouseLeave = useCallback(() => {
    if (startXRef.current !== null) end();
  }, [minDistance, onSwipeLeft, onSwipeRight]);

  const containerProps = {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
  };

  return {
    containerProps,
    dragOffset,
    isDragging,
    didDrag: () => didDragRef.current,
  };
}

